import React, { useState } from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Divider, Flex, Collapse, Tag, Table,
  Thead, Tbody, Tr, Th, Td
} from "@chakra-ui/react";
import {
  FaTint, FaArrowRight, FaChevronDown, FaChevronUp,
  FaExclamationTriangle, FaInfoCircle, FaShieldAlt, FaFlask
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

interface AccordionItemProps {
  heading: string;
  tag: string;
  tagColor: string;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ heading, tag, tagColor, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
      <Box
        as="button"
        w="full"
        textAlign="left"
        p={5}
        onClick={() => setOpen(!open)}
        bg={open ? ICE_MIST : "white"}
        _hover={{ bg: ICE_MIST }}
        transition="background 0.2s"
      >
        <Flex justify="space-between" align="center" gap={4}>
          <HStack spacing={3} flex={1} flexWrap="wrap">
            <Text
              fontSize="xs"
              fontWeight="700"
              px={2}
              py={0.5}
              borderRadius="full"
              bg={tagColor + "20"}
              color={tagColor}
              whiteSpace="nowrap"
            >
              {tag}
            </Text>
            <Text fontWeight="600" color={NAVY} fontSize="sm">{heading}</Text>
          </HStack>
          <Icon
            as={open ? FaChevronUp : FaChevronDown}
            color={DEEP_AQUA}
            flexShrink={0}
            boxSize={3}
          />
        </Flex>
      </Box>
      <Collapse in={open}>
        <Box p={5} pt={0} bg="white" borderTop="1px solid" borderColor="gray.100">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const WaterBlogHardWater: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/hard-water-australia"
        fallbackTitle="Hard Water Australia: The Full Picture — One Water Australia"
        fallbackDescription="Hard water is framed as a simple problem with a simple solution. The full picture is more interesting — and the 'solutions' market has some honest questions to answer."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1552060934-8be0b63c77f3?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Educational Guide
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Hard Water in Australia: The Full Picture
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              Hard water is widely framed as a problem that needs solving. The science is more nuanced — minerals in hard water are beneficial in some contexts, the leading "solutions" have significant caveats, and some products sold for hard water have no credible evidence at all.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Tag bg={WATER_BLUE + "30"} color={WATER_BLUE} fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                Balanced Guide
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                ~11 min read
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                15 citations
              </Tag>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1552060934-8be0b63c77f3?auto=format&fit=crop&w=1200&q=80"
          alt="Shower water — effects of hard water on Australian homes"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* What is hard water */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What Hard Water Actually Is</Heading>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Hard water contains elevated concentrations of dissolved calcium and magnesium — minerals that enter water as it moves through limestone, dolomite, chalk, and other mineral-bearing rock. It is measured in milligrams per litre (mg/L) as calcium carbonate (CaCO₃) equivalents.
            </Text>
            <Box overflowX="auto" mb={6}>
              <Table size="sm" variant="simple">
                <Thead bg={ICE_MIST}>
                  <Tr>
                    <Th color={NAVY} py={3}>Classification</Th>
                    <Th color={NAVY} py={3}>CaCO₃ (mg/L)</Th>
                    <Th color={NAVY} py={3}>Practical effect</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    { cls: "Soft", range: "0–60", effect: "Minimal scale; more corrosive to metallic plumbing" },
                    { cls: "Moderately hard", range: "60–120", effect: "Light scale; most appliances unaffected in short term" },
                    { cls: "Hard", range: "120–180", effect: "Visible scale on fittings; noticeable on skin and hair" },
                    { cls: "Very hard", range: ">180", effect: "Significant scale; appliance damage measurable over years" },
                  ].map((row) => (
                    <Tr key={row.cls} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="600" color={NAVY} fontSize="sm">{row.cls}</Td>
                      <Td fontSize="sm" color={DEEP_AQUA} fontWeight="500">{row.range}</Td>
                      <Td fontSize="sm" color="gray.600">{row.effect}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={5}>
              <Text fontWeight="700" color={NAVY} mb={3} fontSize="sm">Regional hardness across Australia</Text>
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                {[
                  { region: "Melbourne", level: "Soft", range: "10–50 mg/L", note: "Mountain reservoir catchments. Famously soft — and more corrosive to pipes." },
                  { region: "Sydney", level: "Soft–Moderate", range: "40–80 mg/L", note: "Warragamba Dam supply is relatively soft." },
                  { region: "SE QLD (Brisbane / Gold Coast)", level: "Moderate", range: "50–180 mg/L", note: "Varies by zone and desalination contribution. Gold Coast harder than Brisbane north." },
                  { region: "Adelaide", level: "Moderate–Hard", range: "100–200 mg/L", note: "Murray River contribution produces harder water during low-rainfall periods." },
                  { region: "Perth (groundwater areas)", level: "Hard–Very Hard", range: "150–350+ mg/L", note: "Groundwater-sourced areas among the hardest in Australia." },
                  { region: "Toowoomba / Western QLD", level: "Moderate–Hard", range: "100–250 mg/L", note: "Limestone and sedimentary geology produces harder catchment water." },
                ].map((r) => (
                  <Box key={r.region} p={3} bg="white" borderRadius="lg">
                    <Text fontWeight="600" color={NAVY} fontSize="sm">{r.region}</Text>
                    <Text fontSize="xs" color={DEEP_AQUA} fontWeight="600">{r.level} · {r.range}</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>{r.note}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
              <HStack mb={2}>
                <Icon as={FaInfoCircle} color={DEEP_AQUA} />
                <Text fontWeight="700" color="gray.800" fontSize="sm">Before you treat it: know what you actually have</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                Many Australians assume they have hard water because of scale on their shower. In Melbourne and Sydney, most residents have genuinely soft water — and the scale they see is from other causes (evaporation, not hardness). In Perth and parts of QLD, hardness is real and measurable. The starting point for any treatment decision is an actual hardness reading, not assumption.
              </Text>
            </Box>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The health picture — more complex */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>The Health Picture: More Complex Than "Not Dangerous"</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              The standard framing of hard water is "harmless but inconvenient." The full picture involves genuine benefits in some directions, emerging evidence of concern in others, and a serious question about what happens when you remove the minerals.
            </Text>
            <VStack spacing={3} align="stretch">

              <AccordionItem heading="The Cardiovascular Mineral Hypothesis: Hard Water May Be Protective" tag="Potentially beneficial" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    A substantial body of epidemiological research — spanning decades and multiple countries — has found inverse associations between drinking water hardness (specifically magnesium content) and cardiovascular disease mortality. The hypothesis: dietary magnesium and calcium from drinking water contribute to cardiovascular protection.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The WHO's 2009 review <Text as="span" fontStyle="italic">Calcium and Magnesium in Drinking Water: Public Health Significance</Text> concluded that the evidence was sufficient to recommend against removing these minerals from water supplies without replacement. Rubenowitz et al. (2000) found a significant association between low magnesium in drinking water and higher acute myocardial infarction risk in a Swedish population.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    A meta-analysis by Catling et al. (2008) in the <Text as="span" fontStyle="italic">Journal of Water and Health</Text> found consistent evidence for a protective effect of water hardness against cardiovascular disease across multiple study populations. The effect size was modest but statistically robust.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">What this means practically:</Text> The calcium and magnesium in hard water are not contaminants — they are dietary minerals. If you are removing them via water softening, you are removing a nutritional input. Whether this matters depends on your overall diet, but it is not a neutral act.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: WHO (2009) — Calcium and Magnesium in Drinking Water; Catling et al. (2008) — meta-analysis in J Water Health; Rubenowitz et al. (2000) — magnesium and AMI risk, Scand J Work Environ Health
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Eczema and the Skin Barrier: Emerging Research" tag="Concern for some groups" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The relationship between hard water and skin is not simply cosmetic. Research published over the past decade has identified a more substantive mechanism: hard water damages the skin's lipid barrier.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Danby et al. (2018) in the <Text as="span" fontStyle="italic">Journal of Investigative Dermatology</Text> demonstrated that hard water exposure increased the skin's susceptibility to irritation and disrupted the epidermal barrier function. In a mouse model, hard water exposure increased susceptibility to Staphylococcus aureus infection — a significant pathogen in atopic dermatitis (eczema).
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    UK cohort data has found an association between area water hardness and the prevalence of childhood eczema. The BEEP (Barrier Enhancement for Eczema Prevention) trial at King's College London has been investigating whether reducing hard water exposure in infancy can prevent eczema development — treating it as a modifiable environmental risk factor, not just an inconvenience.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The mechanism:</Text> Calcium and magnesium ions interact with soap and skin cleansers to form insoluble soaps (calcium stearate etc.) that deposit on the skin surface. These disrupt the skin's natural lipid layer and alter the skin microbiome, potentially increasing inflammatory responses.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Who this matters for:</Text> People with existing eczema or atopic dermatitis, infants (whose skin barrier is less developed), and families with a history of atopic conditions. For these groups, hard water is not a "cosmetic preference" issue — it is a potentially modifiable health factor.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Danby, S.G. et al. (2018) — JID; Perkin, M.R. et al. — BEEP study background; McNally, N.J. et al. (1998) — eczema and water hardness, Lancet; Miyake, Y. et al. (2004) — water hardness and eczema in Japanese children
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Hard Water and Lead Pipes: The Protective Scale Effect" tag="Often overlooked" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    This is the finding that most hard water articles omit entirely, and it has direct implications for treatment decisions: <Text as="span" fontWeight="600">hard water is protective against lead leaching from pipes and solder.</Text>
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Calcium carbonate deposits on the internal surfaces of pipes form a physical passivating layer that prevents metallic pipe material from dissolving into the water. This scale is the same deposit that appears on your shower head — and inside your pipes it is acting as a barrier between the water and the metal.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Soft water — whether naturally soft (like Melbourne's mountain-catchment supply) or artificially softened — is more aggressive towards metallic plumbing. The Washington DC Water lead crisis, documented by Edwards et al. (2004), was triggered partly by a change in water chemistry (from chlorine to chloramine) that disrupted the existing carbonate scale in distribution pipes, releasing the accumulated lead. The same chemistry applies in household plumbing.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Practical implication:</Text> If you soften your water in a property with pre-1989 copper plumbing and lead solder, you may be increasing the corrosivity of the water that contacts your pipe joins. This argues strongly for not using softened water as your primary drinking water source without a point-of-use filter, and for being aware of any first-draw stagnation effects.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Edwards, M. et al. (2004) — pipe corrosion and disinfectant chemistry, J AWWA; ADWG 2022 — Lead chapter; WHO (2003) — Softening of drinking water: health implications
                  </Text>
                </VStack>
              </AccordionItem>

            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Effects on home */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Real Home Effects: Where the Costs Are</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              The home effects of hard water are well-documented and genuinely worth addressing in high-hardness areas. The cost is real, measurable, and cumulative. In areas with moderate hardness (50–120 mg/L), the calculation is less clear-cut.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={6}>
              {[
                {
                  title: "Limescale build-up",
                  desc: "Calcium carbonate deposits on taps, showerheads, heating elements, and pipe interiors. In very hard water areas (>200 mg/L), heating element scale is measurable and significantly reduces efficiency. In moderate hardness areas, the accumulation rate is slower.",
                  severity: "High in hard areas",
                },
                {
                  title: "Hot water system efficiency",
                  desc: "Scale on electric heating elements is the most economically significant effect. Studies suggest a 1–2mm scale layer can reduce heating efficiency by 10–15%, increasing energy costs. Gas systems with separate heat exchangers are less affected.",
                  severity: "High — measurable cost",
                },
                {
                  title: "Dishwasher and washing machine",
                  desc: "Scale on internal components and pipes. Most modern appliances have some tolerance designed in; the cost is accelerated wear and eventual component failure, particularly of heating elements.",
                  severity: "Moderate over time",
                },
                {
                  title: "Soap and detergent performance",
                  desc: "Calcium and magnesium ions react with soap molecules to form insoluble compounds, reducing lather and requiring more product. Synthetic detergents are less affected than traditional soaps; modern dishwasher and laundry tablets typically include chelating agents that address this.",
                  severity: "Moderate — product-dependent",
                },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="flex-start" mb={2} flexWrap="wrap" gap={2}>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{item.title}</Text>
                    <Text fontSize="xs" fontWeight="600" color={DEEP_AQUA} bg={ICE_MIST} px={2} py={0.5} borderRadius="full">
                      {item.severity}
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.desc}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`}>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                <Text as="span" fontWeight="700">The honest cost calculation:</Text> The economic argument for whole-home softening is strongest in very hard water areas (Perth groundwater areas, parts of western QLD) where appliance damage is accelerated and measurable. In moderate hardness areas (most of SE QLD, Adelaide), the cost-benefit case for a full softening system is less clear, and targeted approaches — descaling appliances, using softener salt in the dishwasher, a shower filter for skin concerns — may provide most of the benefit at a fraction of the cost.
              </Text>
            </Box>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The solutions debate — honest */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>The Solutions Market: What the Evidence Actually Shows</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`} mb={6}>
              <HStack mb={2}>
                <Icon as={FaExclamationTriangle} color={DEEP_AQUA} />
                <Text fontWeight="700" color="gray.800" fontSize="sm">The hard water treatment market contains a wide range of evidence quality</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                At one end: salt-based ion exchange softeners with robust scientific evidence and clear trade-offs. At the other: magnetic and electronic "descalers" with no reliable independent evidence of effectiveness. An honest guide needs to name the difference rather than present all options as equivalently valid.
              </Text>
            </Box>
            <VStack spacing={3} align="stretch">

              <AccordionItem heading="Salt-Based Ion Exchange Softeners: Effective but With Significant Trade-offs" tag="Strong evidence" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Ion exchange softeners are the most effective and well-evidenced technology for removing hardness minerals. Calcium and magnesium ions are exchanged for sodium ions on a resin bed; the resin is periodically regenerated with salt brine. They genuinely work.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The sodium problem:</Text> The sodium added to softened water is proportional to the hardness removed. At 200 mg/L CaCO₃ (moderately hard), a full softener adds approximately 150–160 mg/L of sodium. At 300 mg/L CaCO₃, that figure approaches 230+ mg/L. The WHO's guideline value for sodium in drinking water is 200 mg/L (aesthetic) — softened very hard water can exceed this.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Health implications:</Text> WHO guidance explicitly advises that softened water should not be used for infant formula preparation, and notes concerns for individuals on sodium-restricted diets (hypertension, heart disease, kidney disease). In many European countries, a bypass tap (unsoftened water) is kept in the kitchen specifically for drinking. The WHO (2003) soft water review recommended against using ion exchange-softened water as the sole drinking water source without explicit sodium monitoring.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The mineral removal concern:</Text> By removing calcium and magnesium, softeners produce water that loses the cardiovascular benefits associated with those minerals. This doesn't make softened water "bad" — but it is not nutritionally neutral, and should not be described as such.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The pipe corrosion issue:</Text> Softened water is more corrosive to metallic plumbing than hard water (see the lead/pipe section above). Using softened water throughout a property with older plumbing increases the risk of metallic leaching.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Our recommendation:</Text> If using an ion exchange softener, maintain an unsoftened kitchen tap for drinking and cooking. A point-of-use filter (RO or quality carbon) on the drinking tap addresses both the sodium concern and any residual corrosion-metal risk from property plumbing.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: WHO (2003) — Softening of drinking water: health implications; WHO (2009) — Calcium and Magnesium in Drinking Water; ADWG 2022 — Sodium
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Template-Assisted Crystallisation (TAC): Promising but Contested" tag="Limited evidence" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    TAC (also marketed as dCAL, Filtersorb, ScaleNet, and others) works by converting dissolved calcium and magnesium into microscopic crystals that remain suspended in the water rather than depositing as scale. Critically, it does not remove the minerals — it changes their form.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The evidence:</Text> The Water Quality Research Foundation (WQRF) conducted independent testing of TAC systems in the US (2011) and found measurable scale reduction under controlled conditions. Some subsequent independent testing has been supportive; other studies have found inconsistent results depending on flow rate, contact time, and water chemistry.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">What TAC does not do:</Text> It does not remove hardness minerals, does not add sodium, and does not affect water mineral content for drinking. It specifically addresses scale formation — so the cardiovascular mineral benefit of the water is preserved. It does not address skin effects of hard water (the calcium ions are still present).
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The honest position:</Text> TAC is a legitimate technology with plausible physical chemistry behind it, but the evidence for its effectiveness under all conditions is not as strong as for ion exchange. It is best suited to hard water areas where the primary concern is scale in appliances and pipes — not to addresses where skin or drinking water mineral content are the primary driver.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: WQRF — Template Assisted Crystallization Study (2011); Petrykin, V. et al. — nucleation inhibition studies; independent testing comparisons published in Water Conditioning and Purification International
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Magnetic and Electronic Descalers: Where the Evidence Falls Short" tag="Weak evidence" tagColor={NAVY}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Magnetic water conditioners and electronic descalers are widely sold in Australia, often at significant price points, with claims that magnetic fields or electrical pulses alter water chemistry to prevent scale. They do not require salt, filters, or ongoing maintenance. They sound appealing.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The evidence problem:</Text> Multiple independent peer-reviewed studies have found no reliable scientific evidence that magnetic or electromagnetic treatment alters the crystal structure of calcium carbonate in a way that prevents scale deposition under real-world conditions. Reviews by Coey & Cass (2000) in the journal <Text as="span" fontStyle="italic">Journal of Magnetism and Magnetic Materials</Text> and by Lipus et al. have found inconclusive to negative results in controlled trials.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The mechanism claim:</Text> Proponents claim that magnetic fields convert calcite (adherent scale form) to aragonite (less adherent form). The physical chemistry is theoretically plausible but has not been demonstrated reliably under the range of conditions present in household plumbing. Flow rate, pipe diameter, field strength, and water chemistry all interact in ways that make controlled independent testing difficult.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Our position:</Text> We do not recommend magnetic or electronic descalers as a primary treatment for documented hard water. The market for these products runs on testimonials and the difficulty of falsifying claims about a gradual process. If you have paid for one and believe it works, your scale problem may have resolved for other reasons, or the effect may be specific to your particular water chemistry conditions. We wouldn't stake your appliances or plumbing on it.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Coey, J.M.D. & Cass, S. (2000) — Magnetic water treatment, J Magnetism and Magnetic Materials; Lipus, L.C. et al. — effects of magnetic water treatment; Limpert, G.L. & Raber, J.L. — independent descaler testing
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Reverse Osmosis: The Comprehensive Drinking Water Answer" tag="Strong evidence" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Reverse osmosis removes hardness minerals from drinking water effectively — along with virtually all other dissolved solids. For household use, a point-of-use under-sink RO system is the highest-quality approach to drinking water in hard water areas.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The mineral removal consideration:</Text> Like softened water, RO water has most minerals removed. This is the right choice for drinking water quality in very hard areas (removing calcium carbonate scale potential) but should not be confused with "healthier" — the minerals being removed have nutritional value. A remineralisation post-filter (adding controlled levels of calcium and magnesium back) addresses this for people who want the quality benefits of RO without the complete mineral strip.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Membrane protection:</Text> In chloramine-dosed supplies (SE QLD/Seqwater), an RO system must include a quality pre-carbon stage, because chloramine can damage standard RO membranes more readily than free chlorine. Verify this with the system supplier.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Scope:</Text> Under-sink RO addresses drinking and cooking water only. It does not address scale in appliances, skin effects in the shower, or whole-home chemistry. For whole-home scale protection alongside drinking water quality, a TAC or softener system upstream plus RO at the kitchen tap is a common combination.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    See also: <Text as="span" fontStyle="italic">Reverse Osmosis: A Complete Guide</Text> on this site for detailed system selection guidance.
                  </Text>
                </VStack>
              </AccordionItem>

            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* How to test */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>How to Find Out What You Actually Have</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Treatment decisions should start from actual data. The visual indicators of hard water are unreliable on their own — what looks like scale can result from several causes, and apparent soft-water symptoms (soap performing poorly, dry skin) can have other explanations.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {[
                {
                  method: "Utility zone data",
                  desc: "Seqwater, SA Water, Water Corporation (WA), and other utilities publish hardness data by supply zone. This is the first step — free and immediate. Look for 'hardness' or 'calcium carbonate' in your zone's quality table.",
                  quality: "Good starting point",
                  icon: FaInfoCircle,
                },
                {
                  method: "At-home test strips",
                  desc: "Available from pool shops, hardware stores, and online. Give a ballpark hardness reading at your tap — useful for confirming the utility zone data and catching any last-mile variation. Not precise, but adequate for initial categorisation.",
                  quality: "Useful confirmation",
                  icon: FaFlask,
                },
                {
                  method: "NATA-accredited laboratory test",
                  desc: "An actual tap water sample tested at an accredited laboratory. Gives precise hardness figures alongside TDS, pH, and metal content — the only way to identify last-mile issues from building plumbing. Worth doing before committing to an expensive treatment system.",
                  quality: "Most accurate",
                  icon: FaShieldAlt,
                },
                {
                  method: "Professional water assessment",
                  desc: "On-site assessment with calibrated TDS and hardness meters, plus visual inspection of existing scale, plumbing age, and appliances. Gives context that a lab result alone doesn't provide — identifying where in the property hardness effects are most significant.",
                  quality: "Most comprehensive",
                  icon: FaTint,
                },
              ].map((item) => (
                <Box key={item.method} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                    <HStack>
                      <Icon as={item.icon} color={DEEP_AQUA} />
                      <Text fontWeight="700" color={NAVY} fontSize="sm">{item.method}</Text>
                    </HStack>
                    <Text fontSize="xs" fontWeight="600" color={DEEP_AQUA} bg={ICE_MIST} px={2} py={0.5} borderRadius="full">
                      {item.quality}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.desc}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Decision framework */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Decision Framework: What to Do Based on Your Situation</Heading>
            <VStack spacing={3} align="stretch">
              {[
                {
                  situation: "Soft water area (Melbourne, most of Sydney)",
                  recommendation: "You probably don't have a hard water problem. If you're seeing scale, investigate other causes (shower products, water temperature, evaporation patterns). If you have older plumbing, soft water is more corrosive — consider a point-of-use filter for drinking water quality and occasional first-draw flushing.",
                  action: "No treatment for hardness needed",
                  color: "green",
                },
                {
                  situation: "Moderate hardness (SE QLD 60–120 mg/L, Adelaide inner areas)",
                  recommendation: "Scale accumulation is real but gradual. Appliances benefit from periodic descaling. A dishwasher salt regimen handles most appliance scale. For skin and hair concerns, a shower filter is the most targeted and cost-effective first step. Whole-home softening is unlikely to pay off economically at this hardness level unless you have specific health drivers.",
                  action: "Targeted approach — shower filter + appliance maintenance",
                  color: "blue",
                },
                {
                  situation: "Hard water (120–200 mg/L) — Gold Coast hinterland, Toowoomba, parts of Adelaide",
                  recommendation: "Scale effects are visible and economically relevant. For drinking water quality and reducing mineral effects, an under-sink RO or quality carbon filter is warranted. For whole-home scale protection, TAC conditioner (if scale in pipes/appliances is the primary driver) or a softener with a bypass drinking tap. Verify plumbing age before choosing softener — older copper with lead solder + soft water is a concern.",
                  action: "Under-sink RO + TAC or whole-home consideration",
                  color: "orange",
                },
                {
                  situation: "Very hard water (>200 mg/L) — Perth groundwater, western QLD",
                  recommendation: "Treatment is strongly warranted. Hot water system efficiency loss is measurable; appliance lifespan is shortened. A whole-home TAC or softener with a dedicated unsoftened drinking tap (plus under-sink RO or quality carbon filter) is the most comprehensive approach. Ion exchange softener is appropriate where the sodium addition is managed. NATA lab test first to characterise the full picture.",
                  action: "Whole-home treatment + point-of-use drinking filter",
                  color: "red",
                },
              ].map((item) => (
                <Box key={item.situation} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} fontSize="sm" mb={1}>{item.situation}</Text>
                  <Text fontSize="xs" fontWeight="600" color={DEEP_AQUA} mb={3}>{item.action}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.recommendation}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Closing */}
          <Box p={8} bg={ICE_MIST} borderRadius="2xl" border={`1px solid ${WATER_BLUE}30`}>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color={NAVY}>Our Position on Hard Water</Heading>
              <Text color="gray.700" fontStyle="italic" lineHeight="tall" fontSize="lg">
                "Hard water is not simply a problem to be solved. The minerals that create scale are the same minerals associated with cardiovascular protection in the epidemiological literature. The sodium that replaces them in a standard softener is a genuine concern for many households. And a significant portion of the hard water treatment market is selling products — particularly magnetic and electronic devices — with no reliable scientific evidence. We'll tell you what your water actually contains, whether treatment makes sense in your specific situation, and which approach addresses your actual concerns without creating new ones."
              </Text>
              <Text fontSize="xs" color="gray.500" fontStyle="normal">— One Water Australia</Text>
            </VStack>
          </Box>

          {/* Sources */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Sources & References</Heading>
            <VStack align="flex-start" spacing={1}>
              {[
                "WHO (2009) — Calcium and Magnesium in Drinking Water: Public Health Significance. WHO Press, Geneva",
                "WHO (2003) — Softening of drinking water: health implications. WHO/SDE/WSH/03.06",
                "Catling, L.A. et al. (2008) — A systematic review of analytical observational studies investigating the association between cardiovascular disease and drinking water hardness. J Water Health 6(4)",
                "Rubenowitz, E. et al. (2000) — Magnesium in drinking water in relation to morbidity and mortality from acute myocardial infarction. Epidemiology 11(4)",
                "Danby, S.G. et al. (2018) — Effect of water hardness on skin barrier function, epidermal proteases and their relationship to skin microbiome. J Investig Dermatol",
                "McNally, N.J. et al. (1998) — Atopic eczema and domestic water hardness. Lancet 352(9127)",
                "Miyake, Y. et al. (2004) — Hardness of drinking water and prevalence of atopic eczema in Japanese children. Allergol Int 53(1)",
                "Perkin, M.R. et al. — BEEP (Barrier Enhancement for Eczema Prevention) study protocol and background materials",
                "Edwards, M. et al. (2004) — Corrosion of lead by chloramines in drinking water. J AWWA 96(3)",
                "Australian Drinking Water Guidelines (ADWG), NHMRC & NRMMC 2011 (updated 2022)",
                "Water Quality Research Foundation (WQRF) — Template Assisted Crystallization (TAC) Study (2011)",
                "Coey, J.M.D. & Cass, S. (2000) — Magnetic water treatment. J Magnetism and Magnetic Materials 209(1–3)",
                "Seqwater Annual Drinking Water Quality Report 2025–26 — Zone hardness data",
                "Water Corporation of WA — Perth drinking water quality data (groundwater zones)",
                "SA Water — Adelaide drinking water quality reports and zone hardness data",
              ].map((s) => (
                <Text key={s} fontSize="xs" color="gray.500" lineHeight="tall">• {s}</Text>
              ))}
            </VStack>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Heading size="lg" color="white">Find out what you actually have — before choosing a treatment.</Heading>
              <Text color="whiteAlpha.800" maxW="lg">
                A free assessment gives you a hardness reading for your property, an honest view of whether treatment makes sense, and a recommendation that's matched to your actual water and your actual concerns.
              </Text>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={WATER_BLUE}
                  color={NAVY}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "#8DC5E0" }}
                  transition="all 0.2s"
                >
                  Book a Free Water Assessment
                </Button>
                <Button
                  as={RouterLink}
                  to="/water/blog/reverse-osmosis-guide"
                  variant="outline"
                  borderColor={WATER_BLUE}
                  color={WATER_BLUE}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "whiteAlpha.100" }}
                  rightIcon={<Icon as={FaArrowRight} />}
                >
                  Reverse Osmosis Guide
                </Button>
              </HStack>
            </VStack>
          </Box>

          {/* Related */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Related Reading</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              {[
                { title: "Under-Sink Water Filters", href: "/water/water-filters/under-sink-system" },
                { title: "Reverse Osmosis Guide", href: "/water/blog/reverse-osmosis-guide" },
                { title: "Is Tap Water Safe in Australia?", href: "/water/blog/tap-water-safe-australia" },
                { title: "Gold Coast Water Quality", href: "/water/water-quality/gold-coast" },
                { title: "Chlorine & Chloramine", href: "/water/blog/chlorine-in-tap-water" },
              ].map((l) => (
                <Box
                  key={l.title}
                  as={RouterLink}
                  to={l.href}
                  p={3}
                  bg="gray.50"
                  borderRadius="lg"
                  display="block"
                  textDecoration="none"
                  _hover={{ bg: ICE_MIST }}
                  transition="all 0.2s"
                >
                  <Text fontSize="sm" color={NAVY} fontWeight="500">{l.title}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogHardWater;
